/* eslint-disable global-require */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-var-requires */
import React from 'react'
import styled from 'styled-components'
import { color } from '../styles/color'
import { Table, TableRow, TableHeadCell, TableCell } from './Table'
import { ObservationChangeLog } from '../models/client/observation'
import { Spacer } from './Spacer'
import { TableSkeleton } from './TableSkeleton'
import { device } from '../styles/mixins/device'
import { utcToDisplayDate, utcToMilitaryDisplayTime } from '../util/datetime'
import { useLaptopMediaQuery } from '../styles/mixins/mediaQueryHooks'
import { ContentContainer } from './ContentContainer'
import { Icon } from './Icon/index'
import { text } from '../styles/mixins/text'
import { Skeleton } from './Skeleton'
import { CompanyMembershipDirectory } from '../models/client/company'
import { PaginationContainer } from './PaginationContainer'

const Change = (props: { data: ObservationChangeLog['changes'] }): JSX.Element => {
  const { data } = props
  const nextValue: Record<string, unknown> = {}
  const previousValue: Record<string, unknown> = {}

  const Component = require('react-json-view').default
  Object.entries(data).forEach(([key, value]) => {
    if (!value || !Array.isArray(value)) return

    const [previous, next] = value

    nextValue[key] = next
    previousValue[key] = previous
  })
  return (
    <ChangeContainer>
      <Component name="previous" src={previousValue} collapsed />
      <Component name="next" src={nextValue} collapsed />
    </ChangeContainer>
  )
}

type Props = {
  data: ObservationChangeLog[]
  companyMembers: CompanyMembershipDirectory
  isLoading?: boolean
}

const HEADERS = ['Date', 'Time', 'User', 'Action', 'Details']

const COLUMN_SIZE = 'repeat(4, minmax(100px, 1fr)) 500px'

const ChangeLogTable = ({ data, isLoading, companyMembers }: Props): JSX.Element => {
  if (isLoading) return <TableSkeleton columnCount={4} />

  return (
    <Content>
      <HeaderContent columnSize={COLUMN_SIZE}>
        {HEADERS.map((header) => (
          <TableHeadCell key={header}>{header}</TableHeadCell>
        ))}
      </HeaderContent>
      <ListBody>
        {data.map((log) => {
          const { userId } = log
          const member = userId ? companyMembers[userId] : null

          return (
            <TableRow columnSize={COLUMN_SIZE} key={JSON.stringify(log)}>
              <TableCell>{utcToDisplayDate(log.createdAt)}</TableCell>
              <TableCell>{utcToMilitaryDisplayTime(log.createdAt)}</TableCell>
              <TableCell>{member?.name || '-'}</TableCell>
              <TableCell>
                <Action actionType={log.action}>{log.action}</Action>
              </TableCell>
              <TableCell>
                <Change data={log.changes} />
              </TableCell>
            </TableRow>
          )
        })}
      </ListBody>
    </Content>
  )
}

const LOADING_ITEMS = new Array(20).fill(() => (
  <Line>
    <Column>
      <Skeleton fontSize="paragraph" />
      <Spacer size={1} vertical />
      <Skeleton fontSize="paragraph" />
    </Column>
    <Column>
      <Skeleton fontSize="paragraph" />
      <Spacer size={1} vertical />
      <Skeleton fontSize="paragraph" />
    </Column>
    <Column>
      <Skeleton fontSize="paragraph" />
      <Spacer size={1} vertical />
      <Skeleton fontSize="paragraph" />
    </Column>
  </Line>
))

const ChangelogList = ({ data, isLoading, companyMembers }: Props): JSX.Element =>
  isLoading ? (
    <ListBody>
      {LOADING_ITEMS.map((Component, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={index}>
          <Spacer vertical size={index === 0 ? 2 : 1} />
          <Component />
        </div>
      ))}
    </ListBody>
  ) : (
    <ListBody>
      {data.map((log, index) => {
        const { userId } = log
        const member = userId ? companyMembers[userId] : null

        return (
          <div key={JSON.stringify(log)}>
            <Spacer vertical size={index === 0 ? 2 : 1} />
            <Line>
              <Column>
                <Row>
                  <Icon iconKey="Calendar" color="primary" />
                  <Spacer size={1} />
                  {utcToDisplayDate(log.createdAt)}
                </Row>
                <Spacer vertical size={1} />
                <Row>
                  <Icon iconKey="Clock" color="primary" />
                  <Spacer size={1} />
                  {utcToMilitaryDisplayTime(log.createdAt)}
                </Row>
              </Column>
              <Column>
                <Row>
                  <Icon iconKey="User" color="primary" />
                  <Spacer size={1} />
                  {member?.name || '-'}
                </Row>
                <Spacer vertical size={1} />
                <Row>
                  <Icon iconKey="Change" color="primary" />
                  <Spacer size={1} />
                  <Action actionType={log.action}>{log.action}</Action>
                </Row>
              </Column>
              <ColumnFlexStart>
                <DetailsLabel>Details</DetailsLabel>
                <DetailsContent>{log.comment}</DetailsContent>
              </ColumnFlexStart>
            </Line>
          </div>
        )
      })}
    </ListBody>
  )

export const ChangeLog = (props: Props): JSX.Element => {
  const { data, isLoading, companyMembers } = props
  const isMinLaptop = useLaptopMediaQuery()

  if (isMinLaptop) {
    return (
      <ChangeLogTable data={data} isLoading={Boolean(isLoading)} companyMembers={companyMembers} />
    )
  }
  return (
    <ChangelogList data={data} isLoading={Boolean(isLoading)} companyMembers={companyMembers} />
  )
}

const Content = styled(Table)`
  display: flex;
  flex-flow: column nowrap;
  flex: 0 1 auto;
  min-height: 0px;
  min-width: 0px;

  ${device.desktop`
    max-width: 1000px;
  `}
`

const HeaderContent = styled(TableRow)``

const Line = styled(ContentContainer)`
  display: grid;
  grid-template-columns: 1fr 1fr 2fr;
`

const ListBody = styled.div`
  display: flex;
  flex-flow: column nowrap;
  flex: 0 1 auto;
  min-height: 0px;
  overflow-y: auto;
`

const Row = styled.div`
  display: flex;
  flex-flow: row nowrap;
  flex: 1 1 auto;
  align-items: center;
`

const Column = styled.div`
  display: flex;
  flex-flow: column nowrap;
  flex: 0 0 auto;
  justify-content: center;
`

const ColumnFlexStart = styled.div`
  justify-content: flex-start;
`

const DetailsLabel = styled.div`
  ${text('sub', 'secondary')}
`

const DetailsContent = styled.div``

const Action = styled.span<{ actionType: ObservationChangeLog['action'] }>`
  text-transform: uppercase;
  color: ${(p) => {
    switch (p.actionType) {
      case 'create':
        return color.status.success
      case 'update':
        return color.status.update
      case 'delete':
        return color.status.danger
      default:
        return color.text.primary
    }
  }};
`

const ChangeContainer = styled.span`
  display: flex;
  flex-flow: column nowrap;
  flex: 1 1 auto;
  justify-content: center;
`
